import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@mui/material';
import { DisclosureIcon } from '@wavetrak/theme';
import { getWindow, trackEvent } from '@surfline/web-common';
import Image from 'next/image';
import styles from './SunBumSpfButton.module.scss';
import useSunbumUrl from 'hooks/useSunbumUrl';

// @ts-expect-error Image can't be found on this path
import sunBumLogo from '../../../../public/images/sunbum-logo.svg';

export type SunBumSpfLevels = 30 | 50;

type Props = {
  spfLevel: SunBumSpfLevels;
};

const StartIcon = () => (
  <div className={styles.buttonStartIcon}>
    <Image unoptimized width="20" height="23" src={sunBumLogo} alt="Sun Bum Logo" />
  </div>
);

const SunBumSpfButton: React.FC<Props> = ({ spfLevel }) => {
  const router = useRouter();
  const win = getWindow();

  const SUNBUM_LINK = useSunbumUrl();

  const onUseSpfButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      trackEvent('Clicked CTA', {
        category: 'sunbum',
        ctaText: `Use SPF ${spfLevel}`,
        pageName: 'Spot Report',
        location: 'current conditions',
        path: router?.asPath,
        referrer: `${win?.location?.href ?? ''}${router?.asPath}`,
        search: win?.location.search,
        url: `${win?.location?.href ?? ''}${router?.asPath}`,
      });

      setTimeout(() => {
        // add a delay to ensure the segment event is sent before the new tab is opened which may cause the event to be lost
        window.open(SUNBUM_LINK, '_blank');
      }, 300);
    },
    [router?.asPath, spfLevel, win?.location?.href, win?.location.search],
  );

  return (
    <Button
      className={styles.button}
      data-testid="sunbum-spf-button"
      disableFocusRipple
      disableRipple
      disableTouchRipple
      endIcon={<DisclosureIcon rotation={90} size={11} className={styles.buttonEndIcon} />}
      href={SUNBUM_LINK}
      onClick={onUseSpfButtonClick}
      size="small"
      startIcon={<StartIcon />}
      variant="secondary"
    >
      Use SPF {spfLevel}
    </Button>
  );
};

export default SunBumSpfButton;
